import * as React from 'react';

import { ClassRelationshipItem } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { ModelConceptRelationship } from 'src/containers/ProjectDiagram/types/data';
import { LinkData, Model } from 'src/containers/ProjectDiagram/types/model';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { setLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addDomainOrRangeToRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addDomainOrRangeToRelationship';
import { removeModelConceptRelationshipForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationshipForClass';
import {
  addProjectModelIdsToIri,
  getProjectModelIdsForIri,
} from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { setPropertyType } from 'src/containers/ProjectDiagram/utils/propertyType/setPropertyType';

const doesModelHaveRelationship = (model: Model, relationshipIri: string) =>
  model.linkDataArray.some(
    (linkData) =>
      isModelConceptRelationship(linkData) && linkData.value === relationshipIri
  );

export const addRelationshipItemToClass =
  (classIri: string, item: ClassRelationshipItem) =>
  (model: Model, diagram?: ExtendedDiagram) => {
    if (item.isNewlyCreated && !item.text) {
      return;
    }

    if (!item.isNewlyCreated && !doesModelHaveRelationship(model, item.value)) {
      return;
    }

    const allRelationships: ModelConceptRelationship[] = [];
    item.directions.forEach((direction) => {
      const { addedRelationships, reconnectedRelationships } =
        addDomainOrRangeToRelationship({
          direction,
          classIri,
          relationshipIri: item.value,
          useAllValidLinks: true,
        })(model, diagram);
      allRelationships.push(...addedRelationships, ...reconnectedRelationships);
    });

    if (item.isNewlyCreated) {
      setLabelForIri(item.value, item.text)(model);
      setPropertyType(item.value)(model);
      addProjectModelIdsToIri(
        item.value,
        getProjectModelIdsForIri(classIri, model)
      )(model);
    }
  };

export const useAddRelationshipForClass = ({
  classIri,
}: {
  classIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: ClassRelationshipItem | null) => {
      if (!item || !diagram || !classIri) {
        return;
      }

      commitDiagramModel(diagram, addRelationshipItemToClass(classIri, item));
    },
    [diagram, classIri]
  );
};

export const useRemoveRelationshipForClass = ({
  classIri,
}: {
  classIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: ClassRelationshipItem | null) => {
      if (!item || !diagram || !classIri) {
        return;
      }

      const { value } = item;
      if (!doesModelHaveRelationship(diagram.model, value)) {
        return;
      }

      const classKey = getModelConceptClassKey(classIri);

      // move away from unlinked relationships
      const layoutPartial = new Set([classKey]);
      const layoutLink = (linkData: LinkData) => {
        layoutPartial.add(linkData.key);
        linkData.labelKeys.forEach((labelKey) => {
          layoutPartial.add(labelKey);
        });
      };

      commitDiagramModel(
        diagram,
        (model) => {
          addDeletedData(
            model.linkDataArray.filter((linkData) => {
              if (
                isModelConceptRelationship(linkData) &&
                linkData.value === value &&
                (linkData.to === classKey || linkData.from === classKey)
              ) {
                layoutLink(linkData);
                return true;
              }
              return false;
            })
          )(model);
          removeModelConceptRelationshipForClass({
            direction: null,
            iri: value,
            classIri,
          })(model);
        },
        {
          layout: DiagramModelCommitLayout.PARTIAL,
          layoutPartial,
        }
      );
    },
    [diagram, classIri]
  );
};

export const useUpdateRelationshipForClass = ({
  classIri,
}: {
  classIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: ClassRelationshipItem | null) => {
      if (!item || !diagram || !classIri) {
        return;
      }

      const { value, directions, text } = item;
      if (!text || !doesModelHaveRelationship(diagram.model, value)) {
        return;
      }

      const classKey = getModelConceptClassKey(classIri);

      const directionsToAdd: RelationshipDirection[] = [];
      const directionsToRemove: RelationshipDirection[] = [];

      const addDirection = (direction: RelationshipDirection) => {
        const endProperty =
          direction === RelationshipDirection.OUTGOING ? 'from' : 'to';
        const hasCurrent = diagram.model.linkDataArray.some(
          (linkData) =>
            isModelConceptRelationship(linkData) &&
            linkData.value === value &&
            linkData[endProperty] === classKey
        );
        const hasNext = directions.includes(direction);

        if (!hasCurrent && hasNext) {
          directionsToAdd.push(direction);
        } else if (hasCurrent && !hasNext) {
          directionsToRemove.push(direction);
        }
      };

      addDirection(RelationshipDirection.OUTGOING);
      addDirection(RelationshipDirection.INCOMING);

      // move away from unlinked relationships
      const layoutPartial = new Set(
        directionsToRemove.length ? [classKey] : []
      );
      const layoutLink = (linkData: LinkData) => {
        layoutPartial.add(linkData.key);
        linkData.labelKeys.forEach((labelKey) => {
          layoutPartial.add(labelKey);
        });
      };

      commitDiagramModel(
        diagram,
        (commitModel, diagram) => {
          addDeletedData(
            commitModel.linkDataArray.filter((linkData) => {
              if (
                isModelConceptRelationship(linkData) &&
                linkData.value === value &&
                ((directionsToRemove.includes(RelationshipDirection.INCOMING) &&
                  linkData.to === classKey) ||
                  (directionsToRemove.includes(
                    RelationshipDirection.OUTGOING
                  ) &&
                    linkData.from === classKey))
              ) {
                layoutLink(linkData);
                return true;
              }
              return false;
            })
          )(commitModel);

          directionsToRemove.forEach((direction) => {
            removeModelConceptRelationshipForClass({
              direction,
              iri: value,
              classIri,
            })(commitModel);
          });

          const allRelationships: ModelConceptRelationship[] = [];
          directionsToAdd.forEach((direction) => {
            const { addedRelationships, reconnectedRelationships } =
              addDomainOrRangeToRelationship({
                direction,
                classIri,
                relationshipIri: value,
                useAllValidLinks: true,
              })(commitModel, diagram);
            allRelationships.push(
              ...addedRelationships,
              ...reconnectedRelationships
            );
          });

          setLabelForIri(value, text)(commitModel);
        },
        {
          layout: DiagramModelCommitLayout.PARTIAL,
          layoutPartial,
        }
      );
    },
    [diagram, classIri]
  );
};
